import Axios, { AxiosError } from "axios";
import { useMutation, useQueryClient } from "react-query";
import { segmentKeys } from "src/query-keys";
import { APIError, Segment } from "src/types";

export type SegmentMutationData = Omit<Segment, "segmentId">;

export function useSegmentUpdateMutation(scenarioId: number | string, segmentId: number | string) {
  const queryClient = useQueryClient();

  async function mutationFn(
    scenarioId: number | string,
    segmentId: number | string,
    data: SegmentMutationData
  ): Promise<void> {
    const url = `/planning-api/scenarios/${scenarioId}/segments/${segmentId}`;
    return Axios.put(url, data);
  }

  return useMutation<void, AxiosError<APIError[]>, Segment, unknown>(
    (values: SegmentMutationData) => mutationFn(scenarioId, segmentId, values),
    {
      onMutate: async (updatedSegment: Segment) => {
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries(segmentKeys.detail(scenarioId, segmentId));

        // Optimistically update to the new value
        queryClient.setQueryData(segmentKeys.detail(scenarioId, segmentId), () => updatedSegment);
      },
      onSuccess: async () => {
        queryClient.invalidateQueries(segmentKeys.detail(scenarioId, segmentId));
        return queryClient.refetchQueries(segmentKeys.all(scenarioId), { active: true, exact: true });
      },
    }
  );
}
